import React, { useMemo, useRef, useState } from "react";
import axios from "axios";
// "/pure" entry: plain "@stripe/stripe-js" injects js.stripe.com on IMPORT
// (side effect), which mobile app wrappers hijack on app launch.
import { loadStripe } from "@stripe/stripe-js/pure";
import { Elements, PaymentElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { Button } from "../ui/button";
import {
  X, CreditCard, Link2, Loader2, CheckCircle2, Copy, MessageSquare,
  Mail, ExternalLink, ArrowLeft, AlertCircle,
} from "lucide-react";
import { toast } from "sonner";
import { useTechPermissions, allowed } from "../../contexts/TechPermissionsContext";
import TapToPayDialog from "../payments/TapToPayDialog";
import TapToPayButton from "../payments/TapToPayButton";
import { useTapToPayStatus } from "../../hooks/useTapToPay";

const API = process.env.REACT_APP_BACKEND_URL;
// Lazy Stripe init: loading Stripe.js injects js.stripe.com iframes, which
// mobile app wrappers can hijack as external navigation on app launch.
// Only load it at the moment a card payment actually starts.
let _stripePromises = {};
const getStripe = (stripeAccount) => {
  const pk = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
  if (!pk) return null;
  // Direct-charge PIs live on the connected account — Stripe.js must be
  // initialized with that account for their client_secrets to resolve.
  const key = stripeAccount || "_platform";
  if (!_stripePromises[key]) {
    _stripePromises[key] = loadStripe(pk, stripeAccount ? { stripeAccount } : undefined);
  }
  return _stripePromises[key];
};

const authCfg = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem("tech_token")}` },
});

const money = (v) => `$${(parseFloat(v) || 0).toFixed(2)}`;

// ---------- Inner card form (must live inside <Elements>) ----------
const CardPayForm = ({ chargeAmount, onSucceeded, isDark }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);

  const pay = async () => {
    if (!stripe || !elements || processing) return;
    setProcessing(true);
    try {
      const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        redirect: "if_required",
      });
      if (error) {
        toast.error(error.message || "Card was declined");
        setProcessing(false);
        return;
      }
      if (paymentIntent?.status === "succeeded") {
        await onSucceeded(paymentIntent.id);
      } else if (paymentIntent?.status === "processing") {
        toast.info("Payment is processing — checking status…");
        await onSucceeded(paymentIntent.id);
      } else {
        toast.error("Payment was not completed");
        setProcessing(false);
      }
    } catch (e) {
      toast.error("Payment failed. Please try again.");
      setProcessing(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="collect-card-form">
      <PaymentElement options={{ layout: "tabs" }} />
      <Button
        onClick={pay}
        disabled={!stripe || processing}
        className="w-full h-12 rounded-xl bg-emerald-500 hover:bg-emerald-600 text-white font-semibold text-base"
        data-testid="collect-pay-btn"
      >
        {processing
          ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Processing…</>
          : <><CreditCard className="w-4 h-4 mr-2" /> Charge {money(chargeAmount)}</>}
      </Button>
      <p className="text-[10px] text-muted-foreground text-center">
        Card details are processed securely by Stripe and never stored on this device.
      </p>
    </div>
  );
};

// ---------- Main modal ----------
export const TechCollectPaymentModal = ({ doc, isDark, onClose, onPaid }) => {
  const { permissions } = useTechPermissions();
  const canFull = allowed(permissions, "collect_payments");
  const canPartial = canFull || allowed(permissions, "collect_deposits");

  const total = doc.total || 0;
  const paid = doc.amount_paid || 0;
  const balance = Math.max(total - paid, 0);
  const depositDue = Math.max((doc.deposit_required || 0) - paid, 0);

  const [step, setStep] = useState("amount"); // amount | card | link | success
  const [option, setOption] = useState(canFull ? "balance" : (depositDue > 0 ? "deposit" : "custom"));
  const [customAmount, setCustomAmount] = useState("");
  const [tip, setTip] = useState("");
  const [busy, setBusy] = useState(null); // "card" | "link" | "sms" | "receipt-sms" | "receipt-email"
  const [intentData, setIntentData] = useState(null); // {client_secret, payment_intent_id,...}
  const [linkData, setLinkData] = useState(null); // {checkout_url, amount}
  const [result, setResult] = useState(null); // confirm-payment response
  const [smsSent, setSmsSent] = useState(false);
  const ttp = useTapToPayStatus("tech");
  const [showTTP, setShowTTP] = useState(false);
  const [ttpNotice, setTtpNotice] = useState("");
  const ttpPaidRef = useRef(false);
  const [idemBase] = useState(() =>
    (window.crypto?.randomUUID ? window.crypto.randomUUID() : Math.random().toString(36).slice(2) + Date.now())
  );

  const amount = useMemo(() => {
    if (option === "balance") return balance;
    if (option === "deposit") return depositDue;
    return parseFloat(customAmount) || 0;
  }, [option, customAmount, balance, depositDue]);

  const tipAmount = Math.max(parseFloat(tip) || 0, 0);
  const chargeAmount = Math.round((amount + tipAmount) * 100) / 100;
  const isFull = amount >= balance - 0.005;

  const validate = () => {
    if (amount <= 0) { toast.error("Enter a payment amount greater than zero"); return false; }
    if (amount > balance + 0.005) { toast.error(`Amount exceeds the remaining balance (${money(balance)})`); return false; }
    if (amount < 0.5) { toast.error("Minimum card payment is $0.50"); return false; }
    if (isFull && !canFull) { toast.error("You don't have permission to collect full payments"); return false; }
    return true;
  };

  const payload = () => ({
    amount: Math.round(amount * 100) / 100,
    payment_kind: option,
  });

  const startCard = async () => {
    if (!validate() || busy) return;
    if (!getStripe()) { toast.error("Card payments are not configured"); return; }
    setBusy("card");
    try {
      const res = await axios.post(`${API}/api/tech/documents/${doc.invoice_id}/payment-intent`, {
        ...payload(),
        tip_amount: tipAmount,
        idempotency_key: `${idemBase}_${Math.round(amount * 100)}_${Math.round(tipAmount * 100)}`,
      }, authCfg());
      setIntentData(res.data);
      setStep("card");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start the card payment");
    } finally { setBusy(null); }
  };

  const createLink = async () => {
    if (!validate() || busy) return;
    setBusy("link");
    try {
      const res = await axios.post(
        `${API}/api/tech/documents/${doc.invoice_id}/payment-link`, payload(), authCfg()
      );
      setLinkData(res.data);
      setStep("link");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create payment link");
    } finally { setBusy(null); }
  };

  const confirmPayment = async (paymentIntentId) => {
    try {
      const res = await axios.post(
        `${API}/api/tech/documents/${doc.invoice_id}/confirm-payment`,
        { payment_intent_id: paymentIntentId }, authCfg()
      );
      if (res.data?.success) {
        setResult(res.data);
        setStep("success");
        onPaid && onPaid(res.data);
      } else {
        toast.error(res.data?.message || "Payment was not successful");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not verify the payment");
    }
  };

  const textLink = async () => {
    if (busy) return;
    setBusy("sms");
    try {
      await axios.post(`${API}/api/tech/documents/${doc.invoice_id}/payment-link/sms`, {
        checkout_url: linkData.checkout_url,
        amount: linkData.amount,
      }, authCfg());
      setSmsSent(true);
      toast.success("Payment link texted to the customer");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to text the payment link");
    } finally { setBusy(null); }
  };

  const sendReceipt = async (via) => {
    if (!result?.payment_id || busy) return;
    setBusy(`receipt-${via}`);
    try {
      const res = await axios.post(
        `${API}/api/tech/documents/${doc.invoice_id}/payments/${result.payment_id}/receipt`,
        { via }, authCfg()
      );
      toast.success(res.data?.message || "Receipt sent");
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : (d?.message || "Failed to send receipt"));
    } finally { setBusy(null); }
  };

  const modalBg = isDark ? "bg-[#0c0c14] border-white/[0.08]" : "bg-white border-gray-200 shadow-xl";
  const boxBg = isDark ? "bg-white/[0.02] border-white/[0.05]" : "bg-gray-50 border-gray-200";
  const inputCls = isDark
    ? "bg-white/[0.04] border-white/[0.08] text-white placeholder:text-white/20 focus:border-[#FFCC00]/30"
    : "bg-white border-gray-300 text-gray-900 placeholder:text-gray-400 focus:border-blue-400";
  const optCls = (active) => `w-full flex items-center justify-between px-3.5 py-3 rounded-xl border text-sm transition-all ${
    active
      ? "border-emerald-500/60 bg-emerald-500/10 text-emerald-500 font-semibold"
      : isDark ? "border-white/[0.08] text-white/70 hover:border-white/20" : "border-gray-200 text-gray-700 hover:border-gray-400"
  }`;

  const appearance = {
    theme: isDark ? "night" : "stripe",
    variables: { colorPrimary: "#10B981", borderRadius: "10px" },
  };

  return (
    <div
      className={`fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4 ${isDark ? "bg-black/70" : "bg-black/40"} backdrop-blur-sm`}
      onClick={(e) => { if (e.target === e.currentTarget && step !== "card") onClose(); }}
      data-testid="tech-collect-modal"
    >
      <div className={`w-full sm:max-w-md max-h-[92vh] overflow-y-auto rounded-t-2xl sm:rounded-2xl p-5 space-y-4 border ${modalBg}`}>
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {(step === "card" || step === "link") && (
              <button
                onClick={() => { setStep("amount"); setIntentData(null); setLinkData(null); setSmsSent(false); }}
                className="text-muted-foreground hover:text-foreground"
                data-testid="collect-back-btn"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
            )}
            <h2 className="text-lg font-bold text-foreground font-['Manrope']">
              {step === "success" ? "Payment Collected" : "Collect Payment"}
            </h2>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground" data-testid="collect-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Invoice summary */}
        {step !== "success" && (
          <div className={`grid grid-cols-3 gap-2 rounded-xl p-3 text-center border ${boxBg}`}>
            <div>
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Total</p>
              <p className="text-sm font-bold text-foreground">{money(total)}</p>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Paid</p>
              <p className="text-sm font-bold text-emerald-500">{money(paid)}</p>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Balance</p>
              <p className="text-sm font-bold text-amber-500" data-testid="collect-balance">{money(balance)}</p>
            </div>
          </div>
        )}

        {/* STEP: amount */}
        {step === "amount" && (
          <div className="space-y-4">
            <div className="space-y-2">
              {canFull && (
                <button onClick={() => setOption("balance")} className={optCls(option === "balance")} data-testid="collect-option-balance">
                  <span>Full Balance</span><span className="font-bold">{money(balance)}</span>
                </button>
              )}
              {depositDue > 0 && canPartial && (
                <button onClick={() => setOption("deposit")} className={optCls(option === "deposit")} data-testid="collect-option-deposit">
                  <span>Deposit Due</span><span className="font-bold">{money(depositDue)}</span>
                </button>
              )}
              {canPartial && (
                <button onClick={() => setOption("custom")} className={optCls(option === "custom")} data-testid="collect-option-custom">
                  <span>Custom Amount</span>
                  {option === "custom" && <span className="font-bold">{money(customAmount)}</span>}
                </button>
              )}
            </div>

            {option === "custom" && (
              <input
                type="number" step="0.01" min="0" inputMode="decimal"
                placeholder={`Up to ${money(balance)}`}
                value={customAmount}
                onChange={(e) => setCustomAmount(e.target.value)}
                className={`w-full h-11 rounded-xl px-3 text-base font-semibold border ${inputCls}`}
                data-testid="collect-custom-amount"
              />
            )}

            <div className="space-y-1">
              <label className="text-xs text-muted-foreground">Tip (optional)</label>
              <input
                type="number" step="0.01" min="0" inputMode="decimal"
                placeholder="$0.00"
                value={tip}
                onChange={(e) => setTip(e.target.value)}
                className={`w-full h-10 rounded-xl px-3 text-sm border ${inputCls}`}
                data-testid="collect-tip-input"
              />
            </div>

            <div className={`flex justify-between items-center rounded-xl p-3 border ${boxBg}`}>
              <span className="text-sm text-muted-foreground">Total to charge</span>
              <span className="text-lg font-bold text-foreground" data-testid="collect-charge-total">
                {money(chargeAmount)}
              </span>
            </div>

            <div className="grid grid-cols-1 gap-2">
              {/* Apple reqs 5.1–5.3: Tap to Pay first and always visible in-app */}
              {ttp.native && (
                <TapToPayButton
                  onClick={() => {
                    const s = ttp.status || {};
                    const linked = s.merchant_onboarding?.linked;
                    if (!ttp.eligible || !linked) {
                      setTtpNotice(
                        s.business_eligible && !s.has_permission
                          ? "You don't have the Tap to Pay permission. Contact your admin to enable Tap to Pay for your account."
                          : s.business_eligible && !linked
                            ? "Your business administrator must activate Tap to Pay first (accept Apple's Terms in Settings)."
                            : "Tap to Pay isn't set up for this business yet. Contact your admin to enable it."
                      );
                      return;
                    }
                    setTtpNotice("");
                    if (validate()) setShowTTP(true);
                  }}
                  disabled={!!busy || amount <= 0}
                  dataTestId="collect-tap-to-pay-btn"
                />
              )}
              {ttpNotice && (
                <div className="flex items-start gap-2 rounded-lg border border-amber-500/30 bg-amber-500/5 p-2.5 text-xs text-foreground" data-testid="ttp-admin-notice">
                  <AlertCircle className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" /> {ttpNotice}
                </div>
              )}
              <Button
                onClick={startCard}
                disabled={!!busy || chargeAmount <= 0}
                className="h-12 rounded-xl bg-emerald-500 hover:bg-emerald-600 text-white font-semibold"
                data-testid="collect-charge-card-btn"
              >
                {busy === "card" ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CreditCard className="w-4 h-4 mr-2" />}
                Enter Card Details
              </Button>
              <Button
                onClick={createLink}
                disabled={!!busy || amount <= 0}
                variant="outline"
                className={`h-12 rounded-xl font-semibold ${isDark ? "border-white/[0.12] text-white/80 hover:bg-white/[0.04]" : ""}`}
                data-testid="collect-payment-link-btn"
              >
                {busy === "link" ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Link2 className="w-4 h-4 mr-2" />}
                Send Payment Link
              </Button>
              {tipAmount > 0 && (
                <p className="text-[10px] text-muted-foreground text-center">Tips are only supported on in-person card entry (not payment links{ttp.native ? " or Tap to Pay" : ""}).</p>
              )}
            </div>
          </div>
        )}

        {/* STEP: card (Stripe Elements) */}
        {step === "card" && intentData && (
          <Elements stripe={getStripe(intentData?.stripe_account)} options={{ clientSecret: intentData.client_secret, appearance }}>
            <CardPayForm chargeAmount={chargeAmount} onSucceeded={confirmPayment} isDark={isDark} />
          </Elements>
        )}

        {/* STEP: link */}
        {step === "link" && linkData && (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              Secure Stripe checkout for <span className="font-semibold text-foreground">{money(linkData.amount)}</span>.
              Let the customer pay on their own phone:
            </p>
            <div className={`rounded-xl p-3 border text-xs break-all text-muted-foreground ${boxBg}`} data-testid="collect-link-url">
              {linkData.checkout_url}
            </div>
            <div className="grid grid-cols-1 gap-2">
              <Button
                onClick={textLink}
                disabled={!!busy || smsSent}
                className="h-11 rounded-xl bg-blue-500/15 hover:bg-blue-500/25 text-blue-500 border border-blue-500/20"
                data-testid="collect-link-sms"
              >
                {busy === "sms" ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <MessageSquare className="w-4 h-4 mr-2" />}
                {smsSent ? "Link Texted ✓" : "Text Link to Customer"}
              </Button>
              <div className="grid grid-cols-2 gap-2">
                <Button
                  onClick={() => { navigator.clipboard.writeText(linkData.checkout_url); toast.success("Link copied"); }}
                  variant="outline"
                  className={`h-11 rounded-xl ${isDark ? "border-white/[0.12] text-white/80 hover:bg-white/[0.04]" : ""}`}
                  data-testid="collect-link-copy"
                >
                  <Copy className="w-4 h-4 mr-2" /> Copy
                </Button>
                <Button
                  onClick={() => window.open(linkData.checkout_url, "_blank")}
                  variant="outline"
                  className={`h-11 rounded-xl ${isDark ? "border-white/[0.12] text-white/80 hover:bg-white/[0.04]" : ""}`}
                  data-testid="collect-link-open"
                >
                  <ExternalLink className="w-4 h-4 mr-2" /> Open
                </Button>
              </div>
            </div>
            <p className="text-[10px] text-muted-foreground">
              The invoice updates automatically once the customer completes payment.
            </p>
          </div>
        )}

        {/* STEP: success */}
        {step === "success" && result && (
          <div className="space-y-4 text-center" data-testid="collect-success">
            <div className="flex flex-col items-center gap-2 py-2">
              <div className="w-14 h-14 rounded-full bg-emerald-500/15 flex items-center justify-center">
                <CheckCircle2 className="w-8 h-8 text-emerald-500" />
              </div>
              <p className="text-2xl font-bold text-foreground font-['Manrope']">
                {money((result.amount || 0) + (result.tip_amount || 0))}
              </p>
              <p className="text-xs text-muted-foreground">
                {result.tip_amount > 0 && <>includes {money(result.tip_amount)} tip · </>}
                {result.fully_paid ? "Invoice fully paid" : `Remaining balance ${money(result.balance_due)}`}
              </p>
            </div>
            <div className={`grid grid-cols-2 gap-2 rounded-xl p-3 text-center border ${boxBg}`}>
              <div>
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Paid to Date</p>
                <p className="text-sm font-bold text-emerald-500" data-testid="collect-success-paid">{money(result.amount_paid)}</p>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Balance</p>
                <p className="text-sm font-bold text-amber-500" data-testid="collect-success-balance">{money(result.balance_due)}</p>
              </div>
            </div>
            {result.payment_id && (
              <div className="space-y-2">
                <p className="text-xs text-muted-foreground">Send the customer a receipt:</p>
                <div className="grid grid-cols-2 gap-2">
                  <Button
                    onClick={() => sendReceipt("sms")}
                    disabled={!!busy}
                    variant="outline"
                    className={`h-11 rounded-xl ${isDark ? "border-white/[0.12] text-white/80 hover:bg-white/[0.04]" : ""}`}
                    data-testid="receipt-sms-btn"
                  >
                    {busy === "receipt-sms" ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <MessageSquare className="w-4 h-4 mr-2" />}
                    SMS Receipt
                  </Button>
                  <Button
                    onClick={() => sendReceipt("email")}
                    disabled={!!busy}
                    variant="outline"
                    className={`h-11 rounded-xl ${isDark ? "border-white/[0.12] text-white/80 hover:bg-white/[0.04]" : ""}`}
                    data-testid="receipt-email-btn"
                  >
                    {busy === "receipt-email" ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Mail className="w-4 h-4 mr-2" />}
                    Email Receipt
                  </Button>
                </div>
              </div>
            )}
            <Button
              onClick={onClose}
              className="w-full h-11 rounded-xl bg-[#FFCC00] hover:bg-[#E6B800] text-black font-semibold"
              data-testid="collect-done-btn"
            >
              Done
            </Button>
          </div>
        )}
      </div>

      {/* Tap to Pay (NFC, native mobile app only) */}
      {ttp.eligible && (
        <TapToPayDialog
          open={showTTP}
          onClose={() => { setShowTTP(false); if (ttpPaidRef.current) onClose(); }}
          doc={doc}
          initialAmount={amount}
          api={{
            createIntent: async (amt, reqId) =>
              (await axios.post(`${API}/api/tech/terminal/payment-intents`,
                { doc_id: doc.invoice_id, amount: amt, client_request_id: reqId }, authCfg())).data,
            finalize: async (piId) =>
              (await axios.post(`${API}/api/tech/terminal/payment-intents/${piId}/finalize`, {}, authCfg())).data,
            cancel: async (piId) =>
              (await axios.post(`${API}/api/tech/terminal/payment-intents/${piId}/cancel`, {}, authCfg())).data,
            tokenEndpoint: `${API}/api/tech/terminal/connection-token`,
            authToken: () => localStorage.getItem("tech_token"),
            tosAllowed: false,
            sendReceipt: async (paymentId, via) =>
              axios.post(`${API}/api/tech/documents/${doc.invoice_id}/payments/${paymentId}/receipt`, { via }, authCfg()),
            sendDeclineReceipt: async (piId, via) =>
              axios.post(`${API}/api/tech/terminal/payment-intents/${piId}/decline-receipt`, { via }, authCfg()),
          }}
          onPaid={(res) => { ttpPaidRef.current = true; onPaid && onPaid(res); }}
        />
      )}
    </div>
  );
};

export default TechCollectPaymentModal;
